import { useContext, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { IMPROVE_PLAN_MAX, limitPlanInput, planLength } from "../../../utils/improvePlan";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CloseIcon from "@mui/icons-material/Close";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import ImageIcon from "@mui/icons-material/Image";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import RuleIcon from "@mui/icons-material/Rule";
import SaveIcon from "@mui/icons-material/Save";
import TimelineIcon from "@mui/icons-material/Timeline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { axiosPrivate as axiosInstance } from "../../../../../api/axios";
import { LikelihoodSelect } from './LikelihoodSelect';
import { AssessmentModeContext, GradeSelect, RiskValue, AfterRiskInput } from './GradeAssessment';
import { gradeLabel, hasEvaluationInput as evaluationInputReady } from '../../../utils/assessmentMode';
import {
  getRiskStandardBoxSx,
  getRiskStandardChipSx,
  getRiskStandardStyle,
} from "../../../utils/riskStandard";
import { EmployeeSelect } from "../../../components/common/EmployeeSelect";
import { ManualHelpButton } from "../../../components/common/HelperFunc";
import { isStep1EvaluationComplete, isStep1RowComplete } from "../../../utils/evaluationCompletion";
import { getPendingImprovements, isFinalizedEvaluationRow } from "../../../utils/improvementStatus";
import { assessmentProgress } from "../../../utils/assessmentProgress";

const todayText = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;
};

const todayMonthText = () => todayText().slice(0, 7);
const monthToEvalDate = (month) => `${month || todayMonthText()}-01`;

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const isAfterRiskScoreValid = (value) => {
  const score = Number(value);
  return Number.isInteger(score) && score >= 1 && score <= 20;
};

const getRowKey = (row) => String(row?.eval_id || `cause-${row?.register_cause_id}`);
const getMachineName = (row) =>
  row?.safe_machine_name || row?.machine_name || row?.machine_manage_code || "-";
const getProcessName = (row) => row?.process_name || row?.process_description || "-";
const getRiskScore = (row) =>
  toNumber(row?.risk_score) ||
  toNumber(row?.direct_risk_score) ||
  toNumber(row?.frequency_level) * toNumber(row?.importancy_level);

const getRiskPreview = (row, form = {}, criteria = {}) => {
  const frequency =
    (criteria.frequencies || []).find(
      (item) => Number(item.frequency_id) === Number(form.frequency_id)
    ) || null;
  const importancy =
    (criteria.importancies || []).find(
      (item) => Number(item.importancy_id) === Number(form.importancy_id)
    ) || null;
  const score =
    toNumber(row?.risk_score) ||
    toNumber(row?.direct_risk_score) ||
    toNumber(frequency?.frequency_level || row?.frequency_level) *
      toNumber(importancy?.importancy_level || row?.importancy_level);
  const standard =
    (criteria.standards || []).find(
      (item) =>
        score &&
        score >= toNumber(item.risk_level_min) &&
        score <= toNumber(item.risk_level_max)
    ) || null;

  return { frequency, importancy, score, standard };
};

const rowLineIds = row => String(row.location_ids ?? "").split(",").map(Number).filter(id => Number.isSafeInteger(id) && id > 0);

function lineOptionsFromTree(tree) {
  const options = [];
  const visit = node => {
    const option = { id: Number(node.id), name: node.path || node.location_name, ids: [Number(node.id)] };
    options.push(option);
    for (const child of node.children || []) option.ids.push(...visit(child));
    return option.ids;
  };
  tree.forEach(visit);
  return options;
}

const getStepLineScope = (params, step) => {
  if (step !== 1 || params.get("assessment_scope") !== "line") return null;
  const id = Number(params.get("location_id"));
  const validId = Number.isSafeInteger(id) && id > 0 ? id : null;
  const ids = validId ? Array.from(new Set([
    validId,
    ...String(params.get("location_ids") || "").split(",").map(Number)
      .filter((value) => Number.isSafeInteger(value) && value > 0),
  ])) : [];
  return {
    id: validId,
    ids,
    key: `id-${validId}`,
    name: params.get("location_name")?.trim() || (validId ? `라인 ${validId}` : "라인 미지정"),
  };
};

const getScopeName = (row) =>
  row?.eval_scope === "line"
    ? row?.source_location_name || row?.location_names || "라인"
    : getMachineName(row);

const getHistoryTime = (history = {}) =>
  new Date(history.eval_date || history.approve_date || history.eval_updated_at || 0).getTime() || 0;

const formatRecordedDate = (value) => {
  if (!value) return "기록 없음";
  const text = String(value).trim();
  // SQL wall-clock strings are already in the SAFE database's timezone.
  const local = text.match(/^(\d{4}-\d{2}-\d{2})(?:[ T](\d{2}:\d{2})(:\d{2})?(?:\.\d+)?)?$/);
  if (local) {
    const date = new Date(`${local[1]}T${local[2] || "00:00"}${local[3] || ":00"}Z`);
    if (local[1].startsWith("0000") || Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== local[1]) {
      return "기록 없음";
    }
    return local[2] ? `${local[1]} ${local[2]}${local[3] || ""}` : local[1];
  }
  if (!/(Z|[+-]\d{2}:\d{2})$/i.test(text)) return "기록 없음";
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return "기록 없음";
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Seoul", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
  }).formatToParts(date).map(({ type, value: part }) => [type, part]));
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
};

function EvaluationTiming({ row, detailed = false }) {
  const fields = [
    ["위험요인 등록일시", formatRecordedDate(row?.register_cause_create_date)],
    ["평가 최초 등록일시", row?.eval_id ? formatRecordedDate(row.eval_created_at) : "미평가"],
    ["평가 기준일", row?.eval_id ? formatRecordedDate(row.eval_date) : "미평가"],
    ["평가 최종 수정", row?.eval_id ? formatRecordedDate(row.eval_updated_at) : "미평가"],
  ];
  if (detailed) {
    if (row?.register_asset_create_date) {
      fields.push(["대상 등록일시", formatRecordedDate(row.register_asset_create_date)]);
    }
    [
      ["승인일", row?.approve_date],
      ["개선 등록일시", row?.improve_created_at],
      ["개선 최종 수정", row?.improve_updated_at],
      ["개선 예정일", row?.expected_date],
      ["개선 완료일", row?.comp_date],
    ].forEach(([label, value]) => {
      if (value) fields.push([label, formatRecordedDate(value)]);
    });
  }
  return (
    <Box component="dl" aria-label={`${row?.danger_description || "평가"} 일시`} sx={{
      m: 0, mt: detailed ? 0 : 0.75, minWidth: 0, display: "grid", gap: detailed ? 1 : 0.25,
      gridTemplateColumns: detailed ? { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" } : "minmax(0, 1fr)",
    }}>
      {fields.map(([label, value]) => (
        <Box key={label} sx={{ display: "flex", flexWrap: "wrap", columnGap: 0.75, minWidth: 0 }}>
          <Typography component="dt" variant="caption" color="text.secondary">{label}</Typography>
          <Typography component="dd" variant="caption" sx={{ m: 0, overflowWrap: "anywhere", fontVariantNumeric: "tabular-nums" }}>
            {value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

const getFileViewUrl = (value) => {
  const url = String(value || "").trim();
  if (!url) return "";
  if (/^(https?:|blob:|data:)/i.test(url)) return url;
  const baseURL = String(axiosInstance.defaults.baseURL || "").replace(/\/$/, "");
  if (url.startsWith("/backend/")) {
    return `${baseURL.replace(/\/backend$/, "")}${url}`;
  }
  const path = url.startsWith("/") ? url : `/${url}`;
  return `${baseURL}${path}`;
};

const findEmployeeName = (employees = [], empId) => {
  const targetId = Number(empId);
  if (!targetId) return "-";
  const employee = employees.find((item) => Number(item.emp_id) === targetId);
  if (!employee) return String(empId);
  return [employee.emp_name || employee.emp_id, employee.department_name]
    .filter(Boolean)
    .join(" · ");
};

const getImproveStatusText = (row) => {
  const improveStatus = toNumber(row?.improve_status);
  if (!row?.improve_id && toNumber(row?.requires_improve) !== 1)
    return toNumber(row?.eval_status) < 20 && toNumber(row?.direct_risk_score) === 3 ? "기존 조치 유지" : "개선 불필요";
  if (improveStatus >= 20) return "완료 처리됨";
  if (improveStatus >= 10 || row?.improve_id) return "개선 후 등록 필요";
  return "대책 작성 필요";
};

const getEvalStatusText = (row) => {
  const evalStatus = toNumber(row?.eval_status);
  if (evalStatus >= 20) return "승인 완료";
  if (evalStatus >= 10) return "완료 대기";
  return "평가 작성 중";
};

const stepConfigs = {
  1: {
    title: "위험성평가 실시",
    label: "위험성평가 실시",
    tone: "#7c3aed",
    icon: <RuleIcon />,
    description: "현재 평가 방식에 따라 대상의 위험성을 평가하고 등록합니다.",
    columns: ["대상", "위험요인", "가능성", "중대성", "위험성", "진행 상태", "처리"],
  },
  2: {
    title: "개선대책 관리",
    label: "개선대책 관리",
    tone: "#ea580c",
    icon: <TrendingUpIcon />,
    description: "개선 필요 항목에 대책, 담당자, 예정일, 개선 전 사진을 저장합니다.",
    columns: ["대상", "위험요인", "개선대책", "담당자/일정", "개선 전 사진", "저장"],
  },
  3: {
    title: "개선결과 확인·승인",
    label: "개선결과 확인·승인",
    tone: "#16a34a",
    icon: <AssignmentTurnedInIcon />,
    description: "개선 후 사진과 완료일을 등록한 뒤 최종 승인합니다.",
    columns: ["대상", "위험요인", "평가", "개선 상태", "승인"],
  },
};

function StatCard({
  icon,
  label,
  value,
  hint,
  color = "#2563eb",
  active = false,
  disabled = false,
  onClick,
}) {
  return (
    <Paper
      className="safe-step-summary"
      elevation={0}
      role={onClick ? "button" : undefined}
      tabIndex={onClick && !disabled ? 0 : undefined}
      aria-pressed={onClick ? active : undefined}
      onClick={disabled ? undefined : onClick}
      onKeyDown={(event) => {
        if (!onClick || disabled) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
      sx={{
        position: "relative",
        p: 2,
        border: "1px solid",
        borderColor: active ? color : `${color}26`,
        borderRadius: 2,
        background: active ? `${color}0f` : "linear-gradient(180deg, #fff 0%, #fbfdff 100%)",
        minHeight: 112,
        overflow: "hidden",
        boxShadow: "0 10px 28px rgba(15, 23, 42, 0.04)",
        transition: "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
        cursor: onClick && !disabled ? "pointer" : "default",
        opacity: disabled ? 0.58 : 1,
        "&:hover": {
          borderColor: disabled ? `${color}26` : `${color}55`,
          boxShadow: disabled ? "0 10px 28px rgba(15, 23, 42, 0.04)" : "0 14px 34px rgba(15, 23, 42, 0.07)",
          transform: disabled ? "none" : "translateY(-1px)",
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          bgcolor: color,
        },
      }}
    >
      <Stack spacing={1.25}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 800 }}>
            {label}
          </Typography>
          <Box
            className="safe-step-summary-icon"
            sx={{
              width: 38,
              height: 38,
              borderRadius: "12px",
              display: "grid",
              placeItems: "center",
              color,
              bgcolor: `${color}12`,
              flexShrink: 0,
              "& svg": { fontSize: 22 },
            }}
          >
            {icon}
          </Box>
        </Box>
        <Typography className="safe-step-summary-value" variant="h4" sx={{ fontWeight: 950, lineHeight: 1 }}>
          {value}
        </Typography>
        {hint ? (
          <Typography className="safe-step-summary-hint" variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
            {hint}
          </Typography>
        ) : null}
      </Stack>
    </Paper>
  );
}

function ScopeChip({ row }) {
  const isLine = row?.eval_scope === "line";
  return (
    <Chip
      size="small"
      label={isLine ? "라인" : "설비·구역"}
      color={isLine ? "info" : "default"}
      variant={isLine ? "filled" : "outlined"}
      sx={{ fontWeight: 900, minWidth: 44 }}
    />
  );
}

function StatusChip({ label, color = "default", variant, sx }) {
  return (
    <Chip
      size="small"
      label={label}
      color={color}
      variant={variant || (color === "default" ? "outlined" : "filled")}
      sx={{ fontWeight: 900, ...sx }}
    />
  );
}

function HazardTooltipContent({ row }) {
  return (
    <Box sx={{ maxWidth: 360 }}>
      <Typography sx={{ color: "#fff", fontSize: 13, fontWeight: 900, lineHeight: 1.55 }}>
        {row?.danger_description || "-"}
      </Typography>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
        {[row?.danger_category, row?.danger_cause].filter(Boolean).map((label) => (
          <Box
            key={label}
            sx={{
              px: 0.8,
              py: 0.25,
              borderRadius: 999,
              bgcolor: "rgba(255,255,255,0.12)",
              color: "rgba(255,255,255,0.9)",
              fontSize: 11,
              fontWeight: 800,
            }}
          >
            {label}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

function MiniSectionTitle({ label, color = "text.secondary" }) {
  return (
    <Typography
      variant="caption"
      color={color}
      sx={{ fontWeight: 950, letterSpacing: 0, lineHeight: 1 }}
    >
      {label}
    </Typography>
  );
}

function EmptyRows({ colSpan, title, body }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} sx={{ py: 8, borderBottom: 0 }}>
        <Stack spacing={1} alignItems="center" textAlign="center">
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              color: "text.secondary",
              bgcolor: "grey.100",
            }}
          >
            <PlaylistAddCheckIcon />
          </Box>
          <Typography sx={{ fontWeight: 900 }}>{title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {body}
          </Typography>
        </Stack>
      </TableCell>
    </TableRow>
  );
}

function RowTitle({ row, full = false, asset = false }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, ...(full ? { width: "100%", justifyContent: "flex-start", textAlign: "left", "& > .MuiChip-root": { flexShrink: 0 } } : {}) }}>
      <ScopeChip row={asset ? undefined : row} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 900, ...(full ? { overflowWrap: "anywhere", wordBreak: "keep-all" } : {}) }} noWrap={!full}>
          {asset ? getMachineName(row) : getScopeName(row)}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap={!full} sx={full ? { display: "block", overflowWrap: "anywhere", wordBreak: "keep-all" } : {}}>
          {getProcessName(row)}
        </Typography>
      </Box>
    </Stack>
  );
}

const needsImprovement = (row) => toNumber(row?.requires_improve) === 1;
const isImprovementComplete = (row) => !needsImprovement(row) || toNumber(row?.improve_status) >= 20;
const matchesCompletionView = (row, view) => view === "approval"
  ? isImprovementComplete(row)
  : needsImprovement(row) || Boolean(row?.improve_id);

function buildSummary(rows, step, completionView = "implementation") {
  const lineCount = rows.filter((row) => row?.eval_scope === "line").length;
  const approved = rows.filter((row) => toNumber(row?.eval_status) >= 20).length;

  if (step === 1) {
    const evaluated = rows.filter(isStep1RowComplete).length;
    const pending = rows.length - evaluated;
    return [
      { key: "all", count: rows.length, label: "전체 항목", value: `${rows.length}건`, hint: "현재 평가 주기 기준", icon: <Inventory2Icon />, color: "#2563eb" },
      { key: "pending", count: pending, label: "평가 필요", value: `${pending}건`, hint: "위험성평가 등록 전", icon: <RuleIcon />, color: "#7c3aed" },
      { key: "evaluated", count: evaluated, label: "평가 완료", value: `${evaluated}건`, hint: "위험성평가 등록 완료", icon: <FactCheckIcon />, color: "#16a34a" },
    ];
  }

  if (step === 2) {
    const planSaved = rows.filter((row) => row?.improve_id || row?.improve_plan).length;
    const planNeeded = Math.max(rows.length - planSaved, 0);
    return [
      { key: "all", count: rows.length, label: "대책 작성 대상", value: `${rows.length}건`, hint: "위험성평가에서 개선 필요 판정", icon: <TrendingUpIcon />, color: "#ea580c" },
      { key: "missing", count: planNeeded, label: "대책 미작성", value: `${planNeeded}건`, hint: "대책·담당자·예정일 필요", icon: <WarningAmberIcon />, color: "#dc2626" },
      { key: "saved", count: planSaved, label: "대책 저장됨", value: `${planSaved}건`, hint: "개선대책 관리 입력 완료", icon: <CheckCircleIcon />, color: "#16a34a" },
      { key: "line", count: lineCount, label: "라인 묶음", value: `${lineCount}건`, hint: "라인 평가로 넘어온 항목", icon: <TimelineIcon />, color: "#0891b2" },
    ];
  }

  const completionNeeded = rows.filter((row) => !isImprovementComplete(row)).length;
  if (completionView === "implementation") {
    const completed = rows.length - completionNeeded;
    return [
      { key: "all", count: rows.length, label: "전체 개선 대상", value: `${rows.length}건`, hint: "현재 평가 주기 기준", icon: <Inventory2Icon />, color: "#2563eb" },
      { key: "completion", count: completionNeeded, label: "이행 중", value: `${completionNeeded}건`, hint: "개선 결과 등록 전", icon: <WarningAmberIcon />, color: "#ea580c" },
      { key: "completed", count: completed, label: "이행 완료", value: `${completed}건`, hint: "개선 결과 등록 완료", icon: <CheckCircleIcon />, color: "#16a34a" },
      { key: "line", count: lineCount, label: "라인 평가", value: `${lineCount}건`, hint: "라인 평가로 넘어온 항목", icon: <TimelineIcon />, color: "#0891b2" },
    ];
  }
  const approvalNeeded = rows.length - approved;

  return [
    { key: "all", count: rows.length, label: "전체 확인 대상", value: `${rows.length}건`, hint: "이행 완료 또는 개선 불필요", icon: <Inventory2Icon />, color: "#2563eb" },
    { key: "approval", count: approvalNeeded, label: "승인 대기", value: `${approvalNeeded}건`, hint: "결과 확인 후 승인 필요", icon: <FactCheckIcon />, color: "#ea580c" },
    { key: "approved", count: approved, label: "승인 완료", value: `${approved}건`, hint: "완료 처리와 확인 완료", icon: <CheckCircleIcon />, color: "#16a34a" },
    { key: "line", count: lineCount, label: "라인 묶음", value: `${lineCount}건`, hint: "라인 평가로 넘어온 항목", icon: <TimelineIcon />, color: "#0891b2" },
  ];
}

function EvaluationFields({ row, form = {}, criteria, disabled, onFormChange, table = false }) {
  const gradeMode = criteria.assessment_mode === 'grade';
  const gradeEvaluation = Number(row.direct_risk_score) > 0;
  if (gradeMode || gradeEvaluation) {
    const content = gradeEvaluation && !gradeMode
      ? <Typography variant="body2">등급 평가 · <RiskValue score={row.direct_risk_score} /></Typography>
      : <GradeSelect value={isStep1EvaluationComplete(row) ? row.risk_grade : form.risk_grade}
          colors={criteria.grade_colors} disabled={disabled} onChange={value => onFormChange(row, 'risk_grade', value)} />;
    return table ? <TableCell colSpan={gradeMode ? 1 : 2}>{content}</TableCell> : content;
  }
  const inputs = [
    <LikelihoodSelect key="likelihood" criteria={criteria} {...step1SelectInteractionProps}
      value={form.frequency_id || row.frequency_id || ''} disabled={disabled}
      onChange={event => onFormChange(row, 'frequency_id', event.target.value)} />,
    <TextField key="severity" {...step1SelectInteractionProps} select fullWidth size="small" label="중대성"
      value={form.importancy_id || row.importancy_id || ''} disabled={disabled}
      onChange={event => onFormChange(row, 'importancy_id', event.target.value)}>
      {criteria.importancies.map(item => <MenuItem key={item.importancy_id} value={item.importancy_id}>
        {item.importancy_level} - {item.importancy_description}
      </MenuItem>)}
    </TextField>,
  ];
  return table ? <>{inputs.map((input, index) => <TableCell key={index}>{input}</TableCell>)}</>
    : <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>{inputs}</Box>;
}

function Step1Row({
  row,
  form,
  criteria,
  saving,
  onFormChange,
  onSave,
  onOpenDetail,
}) {
  const hasEvaluationInput = evaluationInputReady(form, criteria.assessment_mode);
  const registered = Boolean(row.eval_id);
  const alreadyEvaluated = isStep1EvaluationComplete(row);
  const editable = !saving;
  const { score, standard } = criteria.assessment_mode === "grade" ? {} : getRiskPreview(row, form, criteria);
  const rowRiskSource =
    !standard && !row?.manage_criteria ? null : standard || { ...row, risk_score: score };
  const saveDisabledReason = alreadyEvaluated
    ? "이미 현재 평가 주기에 위험성평가가 등록된 항목입니다."
    : !hasEvaluationInput
    ? criteria.assessment_mode === "grade" ? "상·중·하 중 하나를 선택하세요." : "가능성과 중대성을 선택하세요."
    : saving
    ? "등록 중입니다."
    : "";

  return (
    <TableRow
      hover
      selected={registered}
      onClick={(event) => {
        if (isInteractiveRowClick(event)) return;
        onOpenDetail?.(row);
      }}
      sx={{ cursor: "pointer" }}
    >
      <TableCell>
        <RowTitle row={row} full asset />
      </TableCell>
      <TableCell>
        <Tooltip
          title={<HazardTooltipContent row={row} />}
          placement="top-start"
          arrow
          enterDelay={250}
          slotProps={{
            tooltip: {
              sx: {
                p: 1.1,
                bgcolor: "#0f172a",
                borderRadius: 2,
                boxShadow: "0 18px 44px rgba(15, 23, 42, 0.32)",
                border: "1px solid rgba(255,255,255,0.1)",
                maxWidth: "none",
              },
            },
            arrow: {
              sx: { color: "#0f172a" },
            },
          }}
        >
          <Typography sx={{ fontWeight: 400, fontSize: 13, lineHeight: 1.65, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
            {row?.danger_description || "-"}
          </Typography>
        </Tooltip>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: '4px', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
          {row?.danger_category || "-"} · {row?.danger_cause || "-"}
        </Typography>
      </TableCell>
      <EvaluationFields row={row} form={form} criteria={criteria} disabled={!editable || alreadyEvaluated} onFormChange={onFormChange} table />
      {criteria.assessment_mode !== "grade" && <TableCell>
        <Stack spacing={0.5}>
          <Typography sx={{ fontWeight: 900 }}><RiskValue score={score} /></Typography>
          {rowRiskSource ? <StatusChip
            label={standard?.manage_criteria || row?.manage_criteria || "기준 선택 전"}
            color="default"
            variant="filled"
            sx={getRiskStandardChipSx(rowRiskSource, criteria.standards)}
          /> : <Typography variant="caption" color="text.secondary">기준 선택 전</Typography>}
        </Stack>
      </TableCell>}
      <TableCell align="center">
        <StatusChip
          {...assessmentProgress(row)}
        />
      </TableCell>
      <TableCell align="center">
        <Tooltip title={saveDisabledReason} disableHoverListener={!saveDisabledReason}>
          <span>
            <Button
              size="small"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={!editable || alreadyEvaluated || saving || !hasEvaluationInput}
              onClick={() => onSave(row)}
              sx={{ fontWeight: 900, whiteSpace: "nowrap" }}
            >
              {saving ? "등록 중" : "평가 등록"}
            </Button>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

function Step1Card({
  row,
  form,
  criteria,
  saving,
  onFormChange,
  onSave,
  onOpenDetail,
}) {
  const hasEvaluationInput = evaluationInputReady(form, criteria.assessment_mode);
  const registered = Boolean(row.eval_id);
  const alreadyEvaluated = isStep1EvaluationComplete(row);
  const editable = !saving;
  const saveDisabledReason = alreadyEvaluated
    ? "이미 현재 평가 주기에 위험성평가가 등록된 항목입니다."
    : !hasEvaluationInput
    ? criteria.assessment_mode === "grade" ? "상·중·하 중 하나를 선택하세요." : "가능성과 중대성을 선택하세요."
    : saving
    ? "등록 중입니다."
    : "";

  const renderRiskPreview = (targetRow, targetForm = {}) => {
    const preview = getRiskPreview(targetRow, targetForm, criteria);
    const standard = preview.standard;
    const neutral = !standard && !targetRow?.manage_criteria;
    const riskSource = neutral
      ? null
      : standard || {
          ...targetRow,
          risk_score: preview.score,
        };
    const riskStyle = getRiskStandardStyle(riskSource, criteria.standards);
    const boxSx = getRiskStandardBoxSx(riskSource, criteria.standards);

    return (
      <Box
        sx={{
          p: 1,
          borderRadius: 1.5,
          bgcolor: boxSx.bgcolor,
          border: "1px solid",
          borderColor: boxSx.borderColor,
          minWidth: 0,
        }}
      >
        <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="space-between">
          <Typography sx={{ fontWeight: 950, color: neutral ? "text.secondary" : riskStyle.fg }}>
            <RiskValue score={preview.score} />
          </Typography>
          <StatusChip
            label={standard?.manage_criteria || targetRow?.manage_criteria || "기준 선택 전"}
            color="default"
            variant="filled"
            sx={getRiskStandardChipSx(riskSource, criteria.standards)}
          />
        </Stack>
      </Box>
    );
  };

  const renderSelects = (targetRow, targetForm = {}, disabled = false) => (
    <EvaluationFields row={targetRow} form={targetForm} criteria={criteria} disabled={disabled} onFormChange={onFormChange} />
  );

  return (
    <Paper
      elevation={0}
      onClick={(event) => {
        if (isInteractiveRowClick(event)) return;
        onOpenDetail?.(row);
      }}
      sx={{
        p: { xs: 1.5, sm: 2 },
        border: "1.5px solid",
        borderColor: registered ? "primary.light" : "divider",
        borderRadius: 2,
        bgcolor: registered ? "#f8fbff" : "#fff",
        boxShadow: "0 12px 30px rgba(15, 23, 42, 0.05)",
        cursor: "pointer",
      }}
    >
      <Stack spacing={1.4}>
        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
            <RowTitle row={row} asset full />
          </Stack>
          <StatusChip
            {...assessmentProgress(row)}
          />
        </Stack>

        <Box>
          <Typography sx={{ fontWeight: 900, lineHeight: 1.4 }}>
            {row?.danger_description || "-"}
          </Typography>
          <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap sx={{ mt: 0.65 }}>
            <StatusChip label={row?.danger_category || "-"} />
            <StatusChip label={row?.danger_cause || "-"} />
          </Stack>
        </Box>

        {renderSelects(row, form, !editable || alreadyEvaluated)}
        {criteria.assessment_mode !== "grade" && renderRiskPreview(row, form)}

        <Tooltip title={saveDisabledReason} disableHoverListener={!saveDisabledReason}>
          <span>
            <Button
              fullWidth
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={!editable || alreadyEvaluated || saving || !hasEvaluationInput}
              onClick={() => onSave(row)}
              sx={{ fontWeight: 900, py: 1.05 }}
            >
              {saving ? "등록 중" : "평가 등록"}
            </Button>
          </span>
        </Tooltip>
      </Stack>
    </Paper>
  );
}

function Step2Row({ row, form, criteria, saving, onFormChange, onSave }) {
  const score = getRiskScore({
    ...row,
    frequency_level:
      criteria.frequencies.find((item) => Number(item.frequency_id) === Number(form.frequency_id))
        ?.frequency_level || row.frequency_level,
    importancy_level:
      criteria.importancies.find((item) => Number(item.importancy_id) === Number(form.importancy_id))
        ?.importancy_level || row.importancy_level,
  });

  const standard = criteria.standards.find(
    (item) =>
      score &&
      score >= toNumber(item.risk_level_min) &&
      score <= toNumber(item.risk_level_max)
  );
  const saveDisabledReason = saving ? "저장 중입니다." : "";

  const step2RiskSource =
    !standard && !row?.manage_criteria ? null : standard || { ...row, risk_score: score };

  return (
    <TableRow hover>
      <TableCell>
        <RowTitle row={row} />
      </TableCell>
      <TableCell sx={{ maxWidth: 360 }}>
        <Tooltip title={row?.danger_description || ""} placement="top-start">
          <Typography noWrap>{row?.danger_description || "-"}</Typography>
        </Tooltip>
      </TableCell>
      <TableCell sx={{ minWidth: 170 }}>
        <TextField
          select
          fullWidth
          size="small"
          label="가능성"
          value={form.frequency_id || ""}
          onChange={(event) => onFormChange(row, "frequency_id", event.target.value)}
        >
          {criteria.frequencies.map((item) => (
            <MenuItem key={item.frequency_id} value={item.frequency_id}>
              {item.frequency_level} - {item.frequency_description}
            </MenuItem>
          ))}
        </TextField>
      </TableCell>
      <TableCell sx={{ minWidth: 170 }}>
        <TextField
          select
          fullWidth
          size="small"
          label="중대성"
          value={form.importancy_id || ""}
          onChange={(event) => onFormChange(row, "importancy_id", event.target.value)}
        >
          {criteria.importancies.map((item) => (
            <MenuItem key={item.importancy_id} value={item.importancy_id}>
              {item.importancy_level} - {item.importancy_description}
            </MenuItem>
          ))}
        </TextField>
      </TableCell>
      <TableCell>
        <Stack spacing={0.5}>
          <Typography sx={{ fontWeight: 900 }}><RiskValue score={score} /></Typography>
          <StatusChip
            label={standard?.manage_criteria || row?.manage_criteria || "기준 선택 전"}
            color="default"
            variant="filled"
            sx={getRiskStandardChipSx(step2RiskSource, criteria.standards)}
          />
        </Stack>
      </TableCell>
      <TableCell>
        {toNumber(standard?.requires_improve ?? row?.requires_improve) === 1
          ? "개선 필요"
          : "허용"}
      </TableCell>
      <TableCell align="center">
        <Tooltip title={saveDisabledReason} disableHoverListener={!saveDisabledReason}>
          <span>
            <Button
              size="small"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={saving}
              onClick={() => onSave(row)}
              sx={{ fontWeight: 900, whiteSpace: "nowrap" }}
            >
              {saving ? "저장 중" : "저장"}
            </Button>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

function ImprovementTiming({ row }) {
  const fields = [["개선 필요 판정일시", row?.improvement_required_at]];
  if (row?.improve_id || row?.improve_plan) {
    fields.push(["대책 등록일시", row?.improve_created_at]);
    if (row?.improve_updated_at && row.improve_updated_at !== row.improve_created_at) {
      fields.push(["대책 최종 수정", row.improve_updated_at]);
    }
  }
  return (
    <Box component="dl" aria-label={`${row?.danger_description || "개선대책"} 개선 이력`} sx={{
      m: 0, display: "flex", flexWrap: "wrap", columnGap: 2.5, rowGap: 0.5,
      borderTop: "1px solid", borderColor: "divider", pt: 1,
    }}>
      {fields.map(([label, value]) => (
        <Box key={label} sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, minWidth: 0 }}>
          <Typography component="dt" variant="caption" color="text.secondary">{label}</Typography>
          <Typography component="dd" variant="caption" sx={{ m: 0, fontVariantNumeric: "tabular-nums", overflowWrap: "anywhere" }}>
            {formatRecordedDate(value)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function Step3Row({ row, form, employees, saving, onFormChange, onUpload, onSave }) {
  const gradeMode = useContext(AssessmentModeContext) === 'grade';
  const saveDisabledReason = saving ? "저장 중입니다." : "";
  const planSaved = Boolean(row?.improve_id || row?.improve_plan);
  const planEditing = Boolean(!planSaved && form?.improve_plan);
  const actionLabel = planSaved ? "개선대책 수정" : "개선대책 등록";
  const riskScore = getRiskScore(row);
  const riskSource = { ...row, risk_score: riskScore };
  const beforePreviewUrl =
    form?.improve_before_preview ||
    form?.improve_before ||
    row?.improve_before ||
    "";

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, md: 2 },
        border: "1px solid",
        borderColor: planSaved ? "primary.light" : "divider",
        borderRadius: 2,
        bgcolor: "#fff",
        boxShadow: "0 10px 26px rgba(15, 23, 42, 0.04)",
        transition: "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
        "&:hover": {
          borderColor: "primary.main",
          boxShadow: "0 14px 34px rgba(15, 23, 42, 0.07)",
          transform: "translateY(-1px)",
        },
      }}
    >
      <Stack spacing={1.5}>
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={1.25}
          alignItems={{ xs: "stretch", lg: "center" }}
          justifyContent="space-between"
        >
          <Box sx={{
            minWidth: 0, flex: 1, display: "grid", gap: 1.5, alignItems: "center",
            gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(240px, 0.9fr) minmax(0, 2fr)" },
          }}>
            <Box sx={{ minWidth: 0, pr: { lg: 1.5 }, alignSelf: "stretch", display: "flex", alignItems: "center", borderRight: { lg: "1px solid" }, borderColor: "divider" }}>
              <RowTitle row={row} full />
            </Box>
            <Box sx={{ minWidth: 0, width: "100%" }}>
              <Tooltip title={row?.danger_description || ""} placement="top-start">
                <Typography
                  sx={{
                    fontWeight: 900,
                    lineHeight: 1.35,
                    wordBreak: "keep-all",
                    overflowWrap: "anywhere",
                  }}
                >
                  {row?.danger_description || "-"}
                </Typography>
              </Tooltip>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={0.75}
                alignItems={{ xs: "stretch", sm: "center" }}
                sx={{
                  mt: 0.6,
                  minWidth: 0,
                  maxWidth: "100%",
                }}
              >
                <Chip
                  size="small"
                  label={<RiskValue score={riskScore} grade={row.risk_grade} prefix="위험성 " />}
                  sx={{
                    ...getRiskStandardChipSx(riskSource),
                    alignSelf: { xs: "flex-start", sm: "center" },
                    maxWidth: "100%",
                    minHeight: 26,
                    "& .MuiChip-label": {
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      px: 1,
                    },
                  }}
                />
                {!gradeMode && <StatusChip
                  label={row?.manage_criteria || "-"}
                  color="default"
                  variant="filled"
                  sx={{
                    ...getRiskStandardChipSx(riskSource),
                    alignSelf: { xs: "stretch", sm: "center" },
                    width: { xs: "100%", sm: "auto" },
                    maxWidth: "100%",
                    height: "auto",
                    minHeight: 28,
                    alignItems: "center",
                    justifyContent: "center",
                    "& .MuiChip-label": {
                      display: "block",
                      width: "100%",
                      whiteSpace: "normal",
                      overflow: "visible",
                      textOverflow: "clip",
                      textAlign: "center",
                      lineHeight: 1.28,
                      py: 0.45,
                      px: 1,
                      wordBreak: "keep-all",
                      overflowWrap: "anywhere",
                    },
                  }}
                />}
              </Stack>
            </Box>
          </Box>

          <Tooltip title={saveDisabledReason} disableHoverListener={!saveDisabledReason}>
            <Box component="span" sx={{ flexShrink: 0, alignSelf: { xs: "flex-end", lg: "center" } }}>
              <Button
                size="small"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={saving}
                onClick={() => onSave(row)}
                sx={{ fontWeight: 900, whiteSpace: "nowrap", alignSelf: { xs: "stretch", md: "flex-start" } }}
              >
                {saving ? "저장 중" : actionLabel}
              </Button>
            </Box>
          </Tooltip>
        </Stack>

        <ImprovementTiming row={row} />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(340px, 1.15fr) minmax(260px, 0.85fr)",
              xl: "minmax(440px, 1.3fr) minmax(300px, 0.9fr) minmax(260px, 0.75fr)",
            },
            gap: 1.5,
            alignItems: "stretch",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1.5,
              bgcolor: "#fff",
              gridColumn: { md: "span 2", xl: "auto" },
            }}
          >
            <Stack spacing={1.25}>
              <MiniSectionTitle label="개선대책" />
              <TextField
                fullWidth
                multiline
                minRows={3}
                size="small"
                placeholder="개선대책을 입력하세요."
                value={form.improve_plan || ""}
                onChange={(event) => onFormChange(row, "improve_plan", limitPlanInput(event.target.value))}
                helperText={`${planLength(form.improve_plan)}/${IMPROVE_PLAN_MAX}자`}
                sx={{ "& .MuiInputBase-root": { bgcolor: "#fff", alignItems: "flex-start" } }}
              />
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1.5,
              bgcolor: "#f8fafc",
            }}
          >
            <Stack spacing={1.75}>
              <MiniSectionTitle label="담당/일정" />
              <EmployeeSelect
                employees={employees}
                value={form.emp_id || ""}
                onChange={(value) => onFormChange(row, "emp_id", value)}
                label="담당자"
                required
                helperText="팀을 먼저 선택하면 담당자를 빠르게 찾을 수 있습니다."
              />
              <TextField
                fullWidth
                size="small"
                type="date"
                label="개선 예정일"
                value={form.expected_date || ""}
                onChange={(event) => onFormChange(row, "expected_date", event.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiInputBase-root": { bgcolor: "#fff", minHeight: 42 } }}
              />
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              border: "1px solid",
              borderColor: planSaved ? "primary.light" : "divider",
              borderRadius: 1.5,
              bgcolor: "#f8fafc",
              gridColumn: { md: "span 2", xl: "auto" },
            }}
          >
            <Stack spacing={1.25}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                useFlexGap
              >
                <MiniSectionTitle label="개선 전 사진" color="text.secondary" />
                <StatusChip
                  label={planSaved ? "대책 저장됨" : planEditing ? "입력 중" : "대책 미작성"}
                  color={planSaved ? "info" : planEditing ? "default" : "warning"}
                />
              </Stack>
              <EvidencePreview
                compact
                hideLabel
                label="개선 전 사진"
                value={beforePreviewUrl}
                emptyText="사진 미리보기 없음"
                imageHeight={128}
              />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                <Button component="label" size="small" variant="outlined" startIcon={<UploadFileIcon />} sx={{ fontWeight: 900 }}>
                  {beforePreviewUrl ? "사진 변경" : "사진 첨부"}
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(event) => onUpload(row, "improve_before", event.target.files?.[0])}
                  />
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                {beforePreviewUrl
                  ? "저장 전에도 선택한 사진을 확인할 수 있습니다."
                  : "사진은 선택 입력입니다."}
              </Typography>
            </Stack>
          </Paper>
        </Box>
      </Stack>
    </Paper>
  );
}

function DetailField({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 900, mt: 0.25 }}>
        {value || "-"}
      </Typography>
    </Box>
  );
}

function EvaluationDateFields({ row }) {
  const month = formatRecordedDate(row?.eval_date).match(/^(\d{4})-(\d{2})-/);
  return (
    <>
      <DetailField label="평가 기준월" value={month ? `${month[1]}년 ${Number(month[2])}월` : "기록 없음"} />
      <DetailField label="평가 등록일시" value={formatRecordedDate(row?.eval_created_at)} />
    </>
  );
}

function RiskHistoryDialog({ open, row, criteria, onClose }) {
  const previousHistory = (Array.isArray(row?.previous_eval_history)
    ? [...row.previous_eval_history]
    : []
  ).sort(
    (a, b) =>
      getHistoryTime(b) - getHistoryTime(a) ||
      toNumber(b.eval_id) - toNumber(a.eval_id)
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 6 }}>
        <Stack spacing={0.5}>
          <Typography variant="h6" sx={{ fontWeight: 950 }}>
            위험요인 상세
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {row ? `${getScopeName(row)} · ${getProcessName(row)}` : "-"}
          </Typography>
        </Stack>
        <IconButton
          aria-label="닫기"
          onClick={onClose}
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "#f8fafc" }}>
        <Stack spacing={1.5}>
          <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <Stack spacing={1}>
              <Typography sx={{ fontWeight: 950 }}>{row?.danger_description || "-"}</Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                <StatusChip label={row?.danger_category || "위험분류 미지정"} />
                <StatusChip label={row?.danger_cause || "위험원인 미지정"} />
                <StatusChip label={row?.eval_scope === "line" ? "라인 평가" : "설비·구역 평가"} color="info" />
                <StatusChip label={getEvalStatusText(row)} color={toNumber(row?.eval_status) >= 20 ? "success" : "default"} />
              </Stack>
              <EvaluationTiming row={row} detailed />
            </Stack>
          </Paper>

          <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <Stack spacing={1.25}>
              <MiniSectionTitle label="이전 평가이력" color="primary.main" />
              {row?.previous_eval_history_start && row?.previous_eval_history_end ? (
                <Typography variant="caption" color="text.secondary">
                  {row.previous_eval_history_start} ~ {row.previous_eval_history_end}
                  {previousHistory.length ? ` · 최근 ${previousHistory.length}건` : ""}
                </Typography>
              ) : null}
              {previousHistory.length ? (
                <Stack spacing={0.75}>
                  {previousHistory.map((history) => {
                    const afterRiskScore = toNumber(history.improve_after_risk);
                    const improvementCompleted =
                      toNumber(history.improve_status) >= 20 || Boolean(history.comp_date);

                    return (
                      <Box
                      key={history.eval_id}
                      sx={{
                        p: 1.25,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1.5,
                        bgcolor: "#fff",
                        display: "grid",
                        gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "120px minmax(0, 1fr) 160px" },
                        gap: 1,
                        alignItems: "center",
                      }}
                    >
                      <DetailField label="평가 기준일" value={formatRecordedDate(history.eval_date)} />
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {history.__line_source ? <StatusChip label={history.__line_source} color="default" /> : null}
                        <StatusChip label={history.eval_scope === "line" ? "라인 평가" : "설비·구역 평가"} color="info" />
                        {criteria.assessment_mode !== 'grade' && (Number(history.direct_risk_score) > 0 ? <StatusChip label="상·중·하 평가" color="default" /> : <>
                          <StatusChip label={`가능성 ${history.frequency_level || "-"}`} color="default" />
                          <StatusChip label={`중대성 ${history.importancy_level || "-"}`} color="default" />
                        </>)}
                        {criteria.assessment_mode !== 'grade' && <StatusChip
                          label={history.manage_criteria || "관리 기준 없음"}
                          color="default"
                          variant="filled"
                          sx={getRiskStandardChipSx(history, criteria?.standards || [])}
                        />}
                      </Stack>
                      <Stack spacing={0.5} alignItems={{ xs: "flex-start", md: "flex-end" }}>
                        <Typography sx={{ fontWeight: 950, textAlign: { xs: "left", md: "right" } }}>
                          <RiskValue score={history.risk_score} grade={history.risk_grade} prefix="개선 전 " />
                        </Typography>
                        {improvementCompleted ? (
                          afterRiskScore ? (
                            <StatusChip
                              label={<RiskValue score={afterRiskScore} grade={history.improve_after_grade} prefix="개선 후 " />}
                              color="default"
                              variant="filled"
                              sx={getRiskStandardChipSx(afterRiskScore, criteria?.standards || [])}
                            />
                          ) : (
                            <StatusChip label="개선 후 위험성 미등록" color="default" />
                          )
                        ) : (
                          <StatusChip label="개선 미등록" color="default" />
                        )}
                      </Stack>
                      <Box sx={{ gridColumn: "1 / -1", minWidth: 0, pt: 1, borderTop: "1px solid", borderColor: "divider" }}>
                        <EvaluationTiming row={history} detailed />
                      </Box>
                      </Box>
                    );
                  })}
                </Stack>
              ) : (
                <Box
                  sx={{
                    p: 1.5,
                    border: "1px dashed",
                    borderColor: "divider",
                    borderRadius: 1.5,
                    bgcolor: "#f8fafc",
                    color: "text.secondary",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    조회 기간에 이전 평가이력이 없습니다.
                  </Typography>
                </Box>
              )}
            </Stack>
          </Paper>

        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ fontWeight: 900 }}>
          닫기
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EvidencePreview({
  label,
  value,
  emptyText = "사진 없음",
  required = false,
  compact = false,
  hideLabel = false,
  imageHeight,
}) {
  const src = getFileViewUrl(value);
  const previewHeight = imageHeight || (compact ? 52 : 190);
  return (
    <Paper
      elevation={0}
      sx={{
        p: hideLabel ? 0 : compact ? 0.5 : 1,
        border: hideLabel ? 0 : "1px solid",
        borderColor: hideLabel ? "transparent" : src ? "divider" : required ? "warning.light" : "divider",
        borderRadius: 1.5,
        bgcolor: src ? "#fff" : required ? "#fffaf0" : "#f8fafc",
        minWidth: compact ? 82 : 0,
        overflow: "hidden",
      }}
    >
      <Stack spacing={0.75}>
        {!hideLabel ? (
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900 }}>
            {label}
          </Typography>
        ) : null}
        {src ? (
          <Box
            component="img"
            src={src}
            alt={label}
            sx={{
              width: "100%",
              height: previewHeight,
              objectFit: "cover",
              borderRadius: hideLabel ? 1.35 : 1,
              border: hideLabel ? 0 : "1px solid rgba(148, 163, 184, 0.24)",
              bgcolor: "#f8fafc",
              display: "block",
            }}
          />
        ) : (
          <Stack
            spacing={0.5}
            alignItems="center"
            justifyContent="center"
            sx={{
              height: previewHeight,
              borderRadius: 1,
              border: hideLabel ? 0 : "1px dashed",
              borderColor: hideLabel ? "transparent" : required ? "warning.main" : "divider",
              color: required ? "warning.dark" : "text.secondary",
              textAlign: "center",
            }}
          >
            <ImageIcon fontSize={compact ? "small" : "medium"} />
            <Typography variant="caption" sx={{ fontWeight: 900 }}>
              {emptyText}
            </Typography>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

function ImprovementDetailDialog({
  open,
  onClose,
  row,
  form,
  employees,
  improvementDone,
  improvementRequired,
}) {
  const beforeUrl =
    form?.improve_before_preview ||
    form?.improve_before ||
    row?.improve_before ||
    "";
  const afterUrl =
    form?.improve_after_preview ||
    form?.improve_after ||
    row?.improve_after ||
    "";
  const assignee = form?.emp_id || row?.emp_id || "";
  const afterRisk = form?.improve_after_risk || row?.improve_after_risk || "";
  const completeDate = form?.comp_date || row?.comp_date || "";
  const completionNote = form?.completion_note || row?.completion_note || "";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 6 }}>
        <Stack spacing={0.5}>
          <Typography variant="h6" sx={{ fontWeight: 950 }}>
            개선대책 상세
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {getScopeName(row)} · {getProcessName(row)}
          </Typography>
        </Stack>
        <IconButton
          aria-label="닫기"
          onClick={onClose}
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "#f8fafc" }}>
        <Stack spacing={2}>
          <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <Stack spacing={1}>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                <StatusChip label={getEvalStatusText(row)} color={toNumber(row?.eval_status) >= 20 ? "success" : "default"} />
                <StatusChip label={getImproveStatusText(row)} color={improvementDone ? "success" : "warning"} />
              </Stack>
              <Typography sx={{ fontWeight: 950 }}>{row?.danger_description || "-"}</Typography>
              <Typography variant="body2" color="text.secondary">
                위험성 <RiskValue score={getRiskScore(row)} grade={row.risk_grade} description={row.manage_criteria} />
              </Typography>
            </Stack>
          </Paper>

          {improvementRequired && !improvementDone ? (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              개선 중 항목입니다. 개선 후 사진, 개선 후 위험성, 완료일을 등록한 뒤
              개선 완료를 등록할 수 있습니다. 승인은 완료 등록 후 별도로 처리합니다.
            </Alert>
          ) : null}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
              gap: 1.5,
            }}
          >
            <Paper elevation={0} sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 2, bgcolor: "#fff" }}>
              <Stack spacing={1.25}>
                <MiniSectionTitle label="개선 전" />
                <EvidencePreview label="개선 전 사진" value={beforeUrl} emptyText="개선 전 사진 없음" />
                <DetailField label="평가 당시 위험성" value={<RiskValue score={getRiskScore(row)} grade={row.risk_grade} description={row.manage_criteria} />} />
                <DetailField label="개선대책" value={form?.improve_plan || row?.improve_plan || "-"} />
                <DetailField label="담당자" value={findEmployeeName(employees, assignee)} />
                <DetailField label="개선 예정일" value={form?.expected_date || row?.expected_date || "-"} />
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                border: "1px solid",
                borderColor: improvementDone ? "success.light" : "warning.light",
                borderRadius: 2,
                bgcolor: improvementDone ? "#f0fdf4" : "#fffaf0",
              }}
            >
              <Stack spacing={1.25}>
                <MiniSectionTitle label="개선 후" color={improvementDone ? "success.dark" : "warning.dark"} />
                <EvidencePreview
                  label="개선 후 사진"
                  value={afterUrl}
                  emptyText={improvementDone ? "개선 후 사진 없음" : "등록 필요"}
                  required={improvementRequired && !improvementDone}
                />
                <DetailField label="개선 후 위험성" value={afterRisk || form?.improve_after_grade ? <RiskValue score={afterRisk} grade={form?.improve_after_grade || row?.improve_after_grade} /> : improvementDone ? "-" : "등록 필요"} />
                <DetailField label="완료일" value={completeDate || (improvementDone ? "-" : "등록 필요")} />
                <DetailField label="승인일" value={row?.approve_date || "-"} />
                <DetailField label="완료 비고" value={completionNote || "-"} />
              </Stack>
            </Paper>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ fontWeight: 900 }}>
          닫기
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Step4Row({
  row,
  form,
  employees,
  saving,
  onFormChange,
  onUpload,
  onSaveImprovement,
  onApprove,
  completionView,
}) {
  const [detailOpen, setDetailOpen] = useState(false);
  const gradeMode = useContext(AssessmentModeContext) === 'grade';
  const isApproved = toNumber(row?.eval_status) >= 20;
  const improvementRequired = needsImprovement(row);
  const improvementDone = isImprovementComplete(row);
  const canApprove = !isApproved && isStep1EvaluationComplete(row) && improvementDone;
  const completionReady = Boolean(
    form?.improve_after &&
      (gradeMode ? gradeLabel(form?.improve_after_grade) : isAfterRiskScoreValid(form?.improve_after_risk)) &&
      form?.comp_date &&
      String(form?.completion_note || "").trim()
  );
  const canCompleteImprovement = completionView === "implementation" && improvementRequired && !improvementDone && !isApproved;
  const beforeUrl =
    form?.improve_before_preview ||
    form?.improve_before ||
    row?.improve_before ||
    "";
  const afterUrl =
    form?.improve_after_preview ||
    form?.improve_after ||
    row?.improve_after ||
    "";
  const completeDisabledReason = saving
    ? "저장 중입니다."
    : !completionReady
    ? "개선 후 사진, 개선 후 위험성, 완료일, 완료 비고를 입력하세요."
    : "";
  const stageLabel = isApproved
    ? "승인 완료"
    : canCompleteImprovement
    ? "완료 처리 필요"
    : canApprove
    ? "승인 필요"
    : "진행 중";
  const stageColor = isApproved
    ? "success"
    : canCompleteImprovement
    ? "warning"
    : canApprove
    ? "info"
    : "default";
  const completeDate = form?.comp_date || row?.comp_date || "";
  const completionNote = form?.completion_note || row?.completion_note || "";

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, md: 2 },
          border: "1px solid",
          borderColor: isApproved ? "success.light" : canCompleteImprovement ? "warning.light" : "divider",
          borderRadius: 2,
          bgcolor: "#fff",
          boxShadow: "0 10px 26px rgba(15, 23, 42, 0.04)",
          transition: "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
          "&:hover": {
            borderColor: isApproved ? "success.main" : canCompleteImprovement ? "warning.main" : "primary.light",
            boxShadow: "0 14px 34px rgba(15, 23, 42, 0.07)",
            transform: "translateY(-1px)",
          },
        }}
      >
        <Stack spacing={1.5}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.25}
            alignItems={{ xs: "stretch", md: "flex-start" }}
            justifyContent="space-between"
          >
            <Stack spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
              <RowTitle row={row} full />
              <Tooltip title={row?.danger_description || ""} placement="top-start">
                <Typography sx={{ fontWeight: 500, overflowWrap: "anywhere" }}>
                  {row?.danger_description || "-"}
                </Typography>
              </Tooltip>
            </Stack>

            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap justifyContent="flex-end">
              <StatusChip label={stageLabel} color={stageColor} />
              {completeDate ? <StatusChip label={`완료일 ${completeDate}`} color="success" /> : null}
              {completionView === "approval" && !isApproved ? (
                <Tooltip
                  title={
                    canApprove
                      ? ""
                      : !improvementDone
                      ? "개선 완료 후 승인할 수 있습니다."
                      : "위험성평가 등록 후 승인할 수 있습니다."
                  }
                >
                  <span>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      disabled={!canApprove || saving}
                      onClick={() => onApprove(row)}
                      sx={{ fontWeight: 900, whiteSpace: "nowrap" }}
                    >
                      확인 후 승인
                    </Button>
                  </span>
                </Tooltip>
              ) : null}
            </Stack>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                lg: canCompleteImprovement
                  ? "minmax(300px, 0.9fr) minmax(420px, 1.1fr)"
                  : "minmax(0, 1fr)",
              },
              gap: 1.25,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 1.25,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1.5,
                bgcolor: "#f8fafc",
              }}
            >
              <Stack spacing={1}>
                <MiniSectionTitle label={canCompleteImprovement ? "개선 전" : "개선 정보"} />
                {improvementRequired || row?.improve_id ? (
                  canCompleteImprovement ? (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "220px minmax(0, 1fr)" },
                        gap: 1.25,
                        alignItems: "stretch",
                      }}
                    >
                      <EvidencePreview compact hideLabel label="개선 전 사진" value={beforeUrl} emptyText="없음" imageHeight={156} />
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.25,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1.25,
                          bgcolor: "#fff",
                        }}
                      >
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                            gap: 1,
                          }}
                        >
                          <DetailField label="현재 위험성" value={<RiskValue score={getRiskScore(row)} grade={row.risk_grade} description={row.manage_criteria} />} />
                          <EvaluationDateFields row={row} />
                          <DetailField label="개선 예정일" value={form?.expected_date || row?.expected_date || "-"} />
                          <DetailField label="담당자" value={findEmployeeName(employees, form?.emp_id || row?.emp_id)} />
                        </Box>
                        <Box sx={{ mt: 1 }}>
                          <DetailField label="개선대책" value={form?.improve_plan || row?.improve_plan || "-"} />
                        </Box>
                      </Paper>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "260px minmax(0, 1fr)" },
                        gap: 1.25,
                        alignItems: "stretch",
                      }}
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                          gap: 1,
                        }}
                      >
                        <EvidencePreview compact label="개선 전" value={beforeUrl} emptyText="없음" imageHeight={136} />
                        <EvidencePreview compact label="개선 후" value={afterUrl} emptyText="없음" imageHeight={136} />
                      </Box>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.25,
                          border: "1px solid",
                          borderColor: "success.light",
                          borderRadius: 1.5,
                          bgcolor: "#fff",
                        }}
                      >
                        <Stack spacing={1}>
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
                              gap: 1,
                            }}
                          >
                            <DetailField label="평가 당시 위험성" value={<RiskValue score={getRiskScore(row)} grade={row.risk_grade} description={row.manage_criteria} />} />
                            <DetailField label="개선 후 위험성" value={<RiskValue score={row?.improve_after_risk} grade={row?.improve_after_grade} />} />
                            <DetailField label="승인 상태" value={isApproved ? "승인 완료" : "승인 필요"} />
                            <EvaluationDateFields row={row} />
                            <DetailField label="완료일" value={completeDate || "-"} />
                            <DetailField label="승인일" value={row?.approve_date ? formatRecordedDate(row.approve_date) : isApproved ? "기록 없음" : "승인 대기"} />
                          </Box>
                          <Divider />
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                              gap: 1,
                            }}
                          >
                            <DetailField label="담당자" value={findEmployeeName(employees, form?.emp_id || row?.emp_id)} />
                            <DetailField label="개선 예정일" value={form?.expected_date || row?.expected_date || "-"} />
                          </Box>
                          <DetailField label="개선대책" value={form?.improve_plan || row?.improve_plan || "-"} />
                          <DetailField label="완료 비고" value={completionNote || "-"} />
                        </Stack>
                      </Paper>
                    </Box>
                  )
                ) : (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.25,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1.5,
                      bgcolor: "#fff",
                    }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
                        gap: 1,
                      }}
                    >
                      <DetailField label="평가 결과" value="현재 안전대책 유지" />
                      <DetailField label="위험성" value={<RiskValue score={getRiskScore(row)} grade={row.risk_grade} description={row.manage_criteria} />} />
                      <DetailField label="승인 상태" value={isApproved ? "승인 완료" : "승인 필요"} />
                      <EvaluationDateFields row={row} />
                      <DetailField label="승인일" value={row?.approve_date ? formatRecordedDate(row.approve_date) : isApproved ? "기록 없음" : "승인 대기"} />
                      <DetailField label="평가 구분" value={row?.eval_scope === "line" ? "라인 평가" : "설비·구역 평가"} />
                    </Box>
                  </Paper>
                )}

                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<VisibilityIcon />}
                  onClick={() => setDetailOpen(true)}
                  sx={{ alignSelf: "flex-start", fontWeight: 900 }}
                >
                  상세보기
                </Button>
              </Stack>
            </Paper>

            {canCompleteImprovement ? (
              <Paper
                elevation={0}
                sx={{
                  p: 1.25,
                  border: "1px solid",
                  borderColor: "warning.light",
                  borderRadius: 1.5,
                  bgcolor: "#fffaf0",
                }}
              >
                <Stack spacing={1}>
                  <MiniSectionTitle label="담당자 · 개선 완료 등록" color="warning.dark" />
                  <EvidencePreview
                    compact
                    hideLabel
                    label="개선 후 사진"
                    value={afterUrl}
                    emptyText="개선 후 사진 필요"
                    required
                    imageHeight={126}
                  />
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Button
                      component="label"
                      size="small"
                      variant="outlined"
                      color={form?.improve_after ? "success" : "warning"}
                      startIcon={<UploadFileIcon />}
                      sx={{ fontWeight: 900 }}
                    >
                      개선 후 사진
                      <input
                        hidden
                        type="file"
                        accept="image/*"
                        onChange={(event) =>
                          onUpload(row, "improve_after", event.target.files?.[0])
                        }
                      />
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                      {form?.improve_after ? "사진 등록됨" : "사진 필요"}
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "minmax(220px, 1fr) minmax(180px, 0.8fr)" },
                      gap: 1,
                      alignItems: "stretch",
                    }}
                  >
                    <AfterRiskInput value={(gradeMode ? form?.improve_after_grade : form?.improve_after_risk) || ""} disabled={saving}
                      onChange={value => onFormChange(row, gradeMode ? "improve_after_grade" : "improve_after_risk", value)} />
                    <TextField
                      size="small"
                      type="date"
                      label="완료일"
                      value={form?.comp_date || ""}
                      onChange={(event) => onFormChange(row, "comp_date", event.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        minWidth: 0,
                        "& .MuiInputBase-root": { minHeight: 52, bgcolor: "#fff" },
                      }}
                    />
                  </Box>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    size="small"
                    label="완료 비고"
                    placeholder="개선 완료 내용이나 확인 메모를 입력하세요."
                    value={form?.completion_note || ""}
                    onChange={(event) => onFormChange(row, "completion_note", event.target.value)}
                    sx={{
                      "& .MuiInputBase-root": { bgcolor: "#fff", alignItems: "flex-start" },
                    }}
                  />
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Tooltip title={completeDisabledReason} disableHoverListener={!completeDisabledReason}>
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          color="warning"
                          startIcon={<SaveIcon />}
                          disabled={saving || !completionReady}
                          onClick={() => onSaveImprovement(row, { complete: true })}
                          sx={{ fontWeight: 900, whiteSpace: "nowrap" }}
                        >
                          {saving ? "저장 중" : "개선 완료 등록"}
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                </Stack>
              </Paper>
            ) : null}
          </Box>
        </Stack>
      </Paper>
      <ImprovementDetailDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        row={row}
        form={form}
        employees={employees}
        improvementDone={improvementDone}
        improvementRequired={improvementRequired}
      />
    </>
  );
}

function filterRowsByStep(rows, step) {
  if (step === 2) {
    return getPendingImprovements(rows);
  }
  if (step === 3 || step === 4) {
    return rows.filter(isStep1EvaluationComplete);
  }
  return rows;
}

const getSearchTextFromRow = (row = {}) => {
  return [
    row?.danger_description,
    row?.danger_category,
    row?.danger_cause,
    row?.manage_criteria,
    getScopeName(row),
    getMachineName(row),
    getProcessName(row),
    row?.source_location_name,
    row?.location_summary,
    row?.location_names,
    ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

const rowMatchesSearch = (row, searchText) => {
  const keyword = String(searchText || "").trim().toLowerCase();
  if (!keyword) return true;
  return getSearchTextFromRow(row).includes(keyword);
};

const isInteractiveRowClick = (event) =>
  Boolean(
    event?.target?.closest?.(
      "button,input,textarea,label,[role='button'],.MuiSelect-select,.MuiInputBase-root,.MuiFormControl-root,.MuiTextField-root,.MuiCheckbox-root,.MuiIconButton-root"
    )
  );

const stopRowClickPropagation = (event) => {
  event?.stopPropagation?.();
};

const step1SelectInteractionProps = {
  onClick: stopRowClickPropagation,
  onMouseDown: stopRowClickPropagation,
  onTouchStart: stopRowClickPropagation,
  SelectProps: {
    MenuProps: {
      onClick: stopRowClickPropagation,
      onMouseDown: stopRowClickPropagation,
      onTouchStart: stopRowClickPropagation,
      MenuListProps: {
        onClick: stopRowClickPropagation,
        onMouseDown: stopRowClickPropagation,
        onTouchStart: stopRowClickPropagation,
      },
    },
  },
};

const rowMatchesSummaryFilter = (row, step, filterKey) => {
  if (!filterKey || filterKey === "all") return true;
  if (filterKey === "line") return Boolean(row.eval_scope === "line");

  if (step === 1) {
    if (filterKey === "pending") return !isStep1RowComplete(row);
    if (filterKey === "evaluated") return isStep1RowComplete(row);
  }

  if (step === 2) {
    const saved = Boolean(row?.improve_id || row?.improve_plan);
    if (filterKey === "missing") return !saved;
    if (filterKey === "saved") return saved;
  }

  if (step === 3 || step === 4) {
    const improvementRequired = needsImprovement(row);
    const improvementDone = isImprovementComplete(row);
    if (filterKey === "completion") return improvementRequired && !improvementDone;
    if (filterKey === "completed") return improvementDone;
    if (filterKey === "approval") return improvementDone && toNumber(row?.eval_status) < 20;
    if (filterKey === "approved") return toNumber(row?.eval_status) >= 20;
  }

  return true;
};

function createInitialForms(rows) {
  return Object.fromEntries(
    rows.map((row) => [
      getRowKey(row),
      {
        risk_grade: row.risk_grade || "",
        improve_after_grade: row.improve_after_grade || "",
        frequency_id: row.frequency_id || "",
        importancy_id: row.importancy_id || "",
        improve_plan: row.improve_plan || "",
        improve_before: row.improve_before || "",
        improve_after: row.improve_after || "",
        emp_id: row.emp_id || "",
        improve_after_risk: row.improve_after_risk || "",
        expected_date: row.expected_date || "",
        comp_date: row.comp_date || "",
        completion_note: row.completion_note || "",
      },
    ])
  );
}

function RiskAssessmentStepPage({ step }) {
  const { group_code, project_code } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const lineScope = useMemo(() => getStepLineScope(searchParams, step), [searchParams, step]);
  const requestedCauseId = searchParams.get("register_cause_id");
  const selectedLines = useMemo(() => {
    const keys = searchParams.getAll("line").filter(key => key === "none" || key === "assigned" || /^id-[1-9]\d*$/.test(key));
    if (lineScope) keys.unshift(lineScope.key);
    if (!keys.length && searchParams.get("filter") === "line") keys.push("assigned");
    return [...new Set(keys)];
  }, [searchParams, lineScope]);
  const requestedFilter = step === 1 && ["pending", "evaluated"].includes(searchParams.get("filter"))
    ? searchParams.get("filter") : "all";
  const config = stepConfigs[step] || stepConfigs[1];
  const [evalMonth, setEvalMonth] = useState(todayMonthText());
  const evalDate = monthToEvalDate(evalMonth);
  const [rows, setRows] = useState([]);
  const [lineOptions, setLineOptions] = useState([]);
  const [lineLoadFailed, setLineLoadFailed] = useState(false);
  const [criteria, setCriteria] = useState({ frequencies: [], importancies: [], standards: [] });
  const [employees, setEmployees] = useState([]);
  const [forms, setForms] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [savingKey, setSavingKey] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [activeSummaryFilter, setActiveSummaryFilter] = useState(requestedFilter);
  const hasCompletionTabs = step === 3 || step === 4;
  const requestedView = searchParams.get("view");
  const focusedRow = requestedCauseId
    ? rows.find((row) => String(row.register_cause_id) === requestedCauseId)
    : null;
  const completionView = ["implementation", "approval"].includes(requestedView)
    ? requestedView
    : focusedRow && isImprovementComplete(focusedRow) ? "approval" : "implementation";
  useEffect(() => setActiveSummaryFilter(requestedFilter), [step, requestedFilter, completionView]);
  const [step1DetailRow, setStep1DetailRow] = useState(null);
  // The permanent sidebar leaves a portrait tablet with less than 700px of
  // usable content width. Switch to the card layout before the table becomes
  // cramped, while keeping the desktop and landscape-tablet table layout.
  const isCompact = useMediaQuery((theme) => theme.breakpoints.down("lg"));

  const reload = () => setReloadKey((prev) => prev + 1);

  useEffect(() => {
    const refresh = () => setReloadKey(n => n + 1);
    window.addEventListener('safe-criteria-updated', refresh);
    return () => window.removeEventListener('safe-criteria-updated', refresh);
  }, []);

  useEffect(() => {
    if (!group_code || !project_code) return undefined;

    let ignore = false;
    setIsLoading(true);
    setFeedback(null);

    const criteriaRequest = axiosInstance.get(`/risk/${group_code}/${project_code}/wizard/risk-criteria`);
    const stepRequest = axiosInstance
      .get(`/risk/${group_code}/${project_code}/wizard/step-items`, {
        params: { eval_date: evalDate },
      })
      .catch((error) => {
        if (error?.response?.status === 404) {
          return axiosInstance.get(`/risk/${group_code}/${project_code}/wizard/work-items`, {
            params: { eval_date: evalDate },
          });
        }
        throw error;
      });
    const pendingRequest =
      step === 1
        ? axiosInstance.get(`/risk/${group_code}/${project_code}/wizard/pending-hazards`, {
            params: { eval_date: evalDate },
          })
        : Promise.resolve({ data: { rows: [] } });

    const treeRequest = step === 1 ? axiosInstance.get(`/risk/${group_code}/${project_code}/location/tree`).catch(() => null) : null;
    Promise.all([criteriaRequest, stepRequest, pendingRequest, treeRequest])
      .then(([criteriaResponse, stepResponse, pendingResponse, treeResponse]) => {
        if (ignore) return;

        const savedRows = (stepResponse.data?.rows || []).map((row) => ({ ...row, __saved: true }));
        const pendingRows = (pendingResponse.data?.rows || []).map((row) => ({
          ...row,
          eval_scope: row.eval_scope || "asset",
          __saved: false,
        }));

        const savedCauseIds = new Set(savedRows.map(row => String(row.register_cause_id)));
        const baseRows = step === 1 ? [...pendingRows.filter(row => !savedCauseIds.has(String(row.register_cause_id))), ...savedRows] : savedRows;
        const nextRows = filterRowsByStep(baseRows, step);
        setLineOptions(lineOptionsFromTree(treeResponse?.data?.tree || []));
        setLineLoadFailed(step === 1 && !treeResponse);

        setCriteria({
          frequencies: criteriaResponse.data?.frequencies || [],
          importancies: criteriaResponse.data?.importancies || [],
          standards: criteriaResponse.data?.standards || [],
          fp: criteriaResponse.data?.fp || null,
          assessment_mode: criteriaResponse.data?.assessment_mode || "score",
          grade_colors: criteriaResponse.data?.grade_colors,
        });
        setEmployees(stepResponse.data?.employees || []);
        setRows(nextRows);
        setForms(createInitialForms(nextRows));
      })
      .catch((error) => {
        if (ignore) return;
        setRows([]);
        setFeedback({
          severity: "error",
          message:
            error?.response?.data?.message ||
            error?.message ||
            "단계 데이터를 불러오지 못했습니다.",
        });
      })
      .finally(() => {
        if (!ignore) setIsLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [evalDate, group_code, project_code, reloadKey, step]);

  const viewRows = useMemo(() => {
    if (hasCompletionTabs) return rows.filter(row => matchesCompletionView(row, completionView));
    if (step !== 1 || !selectedLines.length) return rows;
    const selected = new Set(selectedLines);
    const ids = new Set(lineOptions.filter(line => selected.has(`id-${line.id}`)).flatMap(line => line.ids));
    // Keep bookmarked line links usable even if the line tree cannot be loaded.
    if (lineScope && !lineOptions.some(line => line.id === lineScope.id)) {
      for (const id of lineScope.ids) ids.add(id);
    }
    for (const key of selected) if (/^id-[1-9]\d*$/.test(key)) ids.add(Number(key.slice(3)));
    return rows.filter(row => {
      const locations = rowLineIds(row);
      return locations.length ? selected.has("assigned") || locations.some(id => ids.has(id)) : selected.has("none");
    });
  }, [rows, hasCompletionTabs, completionView, step, selectedLines, lineScope, lineOptions]);

  const lineChoices = [
    { key: "all", label: "전체" },
    ...(selectedLines.includes("assigned") ? [{ key: "assigned", label: "라인 지정 항목" }] : []),
    ...lineOptions.map(line => ({ key: `id-${line.id}`, label: line.name })),
    ...selectedLines.filter(key => key.startsWith("id-") && !lineOptions.some(line => `id-${line.id}` === key))
      .map(key => ({ key, label: key === lineScope?.key ? lineScope.name : `라인 ${key.slice(3)}` })),
    { key: "none", label: "라인 미지정" },
  ];
  const changeLines = (event, option) => {
    const values = event.target.value;
    let keys = (Array.isArray(values) ? values : values.split(",")).filter(key => key !== "all");
    if (option?.props.value === "all") keys = [];
    if (option?.props.value.startsWith("id-")) keys = keys.filter(key => key !== "assigned");
    const next = new URLSearchParams(searchParams);
    const keepScope = lineScope && keys.includes(lineScope.key);
    if (!keepScope) {
      for (const key of ["assessment_scope", "location_id", "location_ids", "location_name"]) next.delete(key);
    }
    next.delete("line");
    if (next.get("filter") === "line") next.delete("filter");
    for (const key of keys) if (!keepScope || key !== lineScope.key) next.append("line", key);
    setSearchParams(next, { replace: true });
    setFeedback(null);
  };
  const summary = useMemo(() => buildSummary(viewRows, step, completionView), [viewRows, step, completionView]);
  const displayedRows = useMemo(
    () =>
      viewRows.filter(
        (row) =>
          (!requestedCauseId || String(row.register_cause_id) === requestedCauseId) &&
          rowMatchesSummaryFilter(row, step, activeSummaryFilter) &&
          rowMatchesSearch(row, searchText)
      ),
    [activeSummaryFilter, viewRows, searchText, step, requestedCauseId]
  );

  const handleCompletionViewChange = (_event, value) => {
    const next = new URLSearchParams(searchParams);
    next.set("view", value);
    setSearchParams(next, { replace: true });
    setActiveSummaryFilter("all");
    setFeedback(null);
  };

  const handleSummaryClick = (item) => {
    setActiveSummaryFilter(item.key || "all");
    if (!item.count) {
      setFeedback({
        severity: "info",
        message: `${item.label} 항목이 없습니다.`,
      });
      return;
    }
    setFeedback(null);
  };

  const updateForm = (row, field, value) => {
    const key = getRowKey(row);
    setForms((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        [field]: value,
      },
    }));
  };

  const uploadFile = async (row, field, file) => {
    if (!file) return;
    const key = getRowKey(row);
    const previewField = `${field}_preview`;
    const previewUrl =
      typeof URL !== "undefined" &&
      typeof Blob !== "undefined" &&
      file instanceof Blob
        ? URL.createObjectURL(file)
        : "";
    if (previewUrl) updateForm(row, previewField, previewUrl);
    setSavingKey(`${key}-${field}`);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("kind", field);
      const response = await axiosInstance.post(
        `/risk/${group_code}/${project_code}/wizard/file`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      updateForm(row, field, response.data?.url || "");
      if (previewUrl) {
        updateForm(row, previewField, "");
        URL.revokeObjectURL(previewUrl);
      }
      setFeedback({ severity: "success", message: "사진이 업로드되었습니다." });
    } catch (error) {
      if (previewUrl) {
        updateForm(row, previewField, "");
        URL.revokeObjectURL(previewUrl);
      }
      setFeedback({
        severity: "error",
        message: error?.response?.data?.message || "사진 업로드 중 오류가 발생했습니다.",
      });
    } finally {
      setSavingKey("");
    }
  };

  const saveEvaluation = async (row, overrides = {}) => {
    const key = getRowKey(row);
    const form = forms[key] || {};
    if (!isStep1EvaluationComplete(row) && !evaluationInputReady(form, criteria.assessment_mode)) {
      setFeedback({
        severity: "warning",
        message: criteria.assessment_mode === "grade" ? "상·중·하 중 하나를 먼저 선택하세요." : "가능성과 중대성을 먼저 선택하세요.",
      });
      return;
    }

    setSavingKey(key);
    try {
      await axiosInstance.post(`/risk/${group_code}/${project_code}/wizard/risk-evaluations`, {
        eval_date: evalDate,
        assessment_mode: criteria.assessment_mode || "score",
        ...(criteria.fp && criteria.assessment_mode !== "grade" ? { fp_version: criteria.fp.version } : {}),
        evaluations: [{
          eval_id: row.eval_id,
          register_cause_id: row.register_cause_id,
          ...(criteria.assessment_mode === "grade"
            ? (!isStep1EvaluationComplete(row) ? { risk_grade: form.risk_grade } : {})
            : { frequency_id: form.frequency_id, importancy_id: form.importancy_id }),
          eval_status:
            overrides.eval_status ??
            Math.max(toNumber(row.eval_status), 10),
          approve_date: overrides.approve_date || row.approve_date || null,
          eval_date: evalDate,
          eval_scope: row.eval_scope || "asset",
          source_location_id: row.source_location_id || null,
          wizard_step: overrides.wizard_step || 2,
          wizard_group_key: row.wizard_group_key || null,
          upsert: true,
          improvement: overrides.improvement,
        }],
      });
      setFeedback({
        severity: "success",
        message: overrides.successMessage || "위험성평가가 등록되었습니다.",
      });
      reload();
    } catch (error) {
      setFeedback({
        severity: "error",
        message: error?.response?.data?.message || "위험성평가 등록 중 오류가 발생했습니다.",
      });
    } finally {
      setSavingKey("");
    }
  };

  const saveImprovement = async (row, options = {}) => {
    const key = getRowKey(row);
    const form = forms[key] || {};
    const complete = Boolean(options.complete);
    const planSaved = Boolean(row.improve_id || row.improve_plan);
    if (!complete && isFinalizedEvaluationRow(row)) {
      setFeedback({
        severity: "info",
        message: "이미 완료된 항목은 개선대책 관리에서 수정할 수 없습니다. 개선결과 확인·승인에서 상세 내용을 확인하세요.",
      });
      return;
    }
    if (!isStep1EvaluationComplete(row)) {
      setFeedback({ severity: "warning", message: "위험성평가를 먼저 등록하세요." });
      return;
    }
    if (planLength(form.improve_plan) > IMPROVE_PLAN_MAX) {
      setFeedback({ severity: "warning", message: `개선대책 내용은 ${IMPROVE_PLAN_MAX}자 이내로 입력하세요.` });
      return;
    }
    if (!form.improve_plan?.trim() || !form.emp_id || !form.expected_date) {
      setFeedback({ severity: "warning", message: "개선대책, 담당자, 개선 예정일을 입력하세요." });
      return;
    }
    if (
      complete &&
      (!form.improve_after ||
        !(criteria.assessment_mode === 'grade' ? gradeLabel(form.improve_after_grade) : isAfterRiskScoreValid(form.improve_after_risk)) ||
        !form.comp_date ||
        !String(form.completion_note || "").trim())
    ) {
      setFeedback({
        severity: "warning",
        message: "개선 후 사진, 개선 후 위험성, 완료일, 완료 비고를 입력하세요.",
      });
      return;
    }

    const keepCompleted = !complete && toNumber(row.improve_status) >= 20;
    await saveEvaluation(row, {
      wizard_step: complete ? 4 : 3,
      eval_status: row.eval_status || 10,
      approve_date: row.approve_date || null,
      successMessage: complete
        ? "개선 완료가 저장되었습니다. 승인 담당자의 확인을 기다립니다."
        : planSaved
        ? "개선대책이 수정되었습니다."
        : "개선대책이 등록되었습니다.",
      improvement: {
        improve_status: complete || keepCompleted ? 20 : 10,
        improve_plan: form.improve_plan,
        improve_before: form.improve_before || null,
        improve_after: complete || keepCompleted ? form.improve_after || row.improve_after || null : row.improve_after || null,
        emp_id: form.emp_id,
        ...(criteria.assessment_mode === 'grade'
          ? { improve_after_grade: (complete || keepCompleted ? form.improve_after_grade : row.improve_after_grade) || null }
          : { improve_after_risk: (complete || keepCompleted ? form.improve_after_risk || row.improve_after_risk : row.improve_after_risk) || null }),
        expected_date: form.expected_date,
        comp_date: complete || keepCompleted ? form.comp_date || row.comp_date || null : row.comp_date || null,
        completion_note:
          complete || keepCompleted
            ? form.completion_note || row.completion_note || null
            : row.completion_note || null,
      },
    });
  };

  const approveEvaluation = async (row) => {
    const key = getRowKey(row);
    const form = forms[key] || {};
    const improvementRequired = toNumber(row.requires_improve) === 1;
    if (improvementRequired && toNumber(row.improve_status) < 20) {
      setFeedback({ severity: "warning", message: "개선 완료 후 승인할 수 있습니다." });
      return;
    }

    await saveEvaluation(row, {
      wizard_step: 4,
      eval_status: 20,
      approve_date: todayText(),
      successMessage: "위험성평가가 승인되었습니다.",
      improvement: row.improve_id
        ? {
            improve_status: row.improve_status,
            improve_plan: row.improve_plan,
            improve_before: row.improve_before || form.improve_before || null,
            improve_after: row.improve_after || form.improve_after || null,
            emp_id: row.emp_id || form.emp_id || null,
            ...(criteria.assessment_mode === 'grade'
              ? { improve_after_grade: row.improve_after_grade || null }
              : { improve_after_risk: row.improve_after_risk || form.improve_after_risk || null }),
            expected_date: row.expected_date || form.expected_date || null,
            comp_date: row.comp_date || form.comp_date || null,
            completion_note: row.completion_note || form.completion_note || null,
          }
        : undefined,
    });
  };

  const renderRow = (row, index) => {
    const key = getRowKey(row);
    const form = forms[key] || {};
    const saving = savingKey === key || savingKey.startsWith(`${key}-`);

    if (step === 1) {
      const commonProps = {
        row,
        form,
        criteria,
        saving,
        onFormChange: updateForm,
        onSave: saveEvaluation,
        onOpenDetail: setStep1DetailRow,
      };

      return isCompact ? (
        <Step1Card key={`${key}-${index}`} {...commonProps} />
      ) : (
        <Step1Row key={`${key}-${index}`} {...commonProps} />
      );
    }
    if (step === 2) {
      return (
        <Step3Row
          key={`${key}-${index}`}
          row={row}
          form={form}
          employees={employees}
          saving={saving}
          onFormChange={updateForm}
          onUpload={uploadFile}
          onSave={saveImprovement}
        />
      );
    }
    if (step === 3 || step === 4) {
      return (
        <Step4Row
          key={`${key}-${index}`}
          row={row}
          form={form}
          employees={employees}
          saving={saving}
          onFormChange={updateForm}
          onUpload={uploadFile}
          onSaveImprovement={saveImprovement}
          onApprove={approveEvaluation}
          completionView={completionView}
        />
      );
    }
    return (
      <Step2Row
        key={`${key}-${index}`}
        row={row}
        form={form}
        criteria={criteria}
        saving={saving}
        onFormChange={updateForm}
        onSave={saveEvaluation}
      />
    );
  };

  const useCardLayout = isCompact || step === 2 || step === 3 || step === 4;

  return (
    <AssessmentModeContext.Provider value={criteria.assessment_mode || "score"}>
    <Box className="safe-assessment-step" sx={{ minHeight: "100%", flexShrink: hasCompletionTabs ? 0 : undefined, p: { xs: 1.5, md: 3 }, bgcolor: "#f6f8fb" }}>
      <Stack
        spacing={2}
        sx={{
          width: "100%",
          maxWidth: { xs: 900, lg: 1280 },
          mx: "auto",
        }}
      >
        <Paper className="safe-step-header"
          elevation={0}
          sx={{
            p: { xs: 2, md: 2.5 },
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            bgcolor: "#fff",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  color: config.tone,
                  bgcolor: `${config.tone}14`,
                  flexShrink: 0,
                }}
              >
                {config.icon}
              </Box>
              <Box>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>
                    {config.title}
                  </Typography>
                  <ManualHelpButton title={config.title} />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {config.description}
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
              <TextField
                label="기준 월"
                size="small"
                type="month"
                value={evalMonth}
                onChange={(event) => setEvalMonth(event.target.value || todayMonthText())}
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: "100%", sm: 160 } }}
              />
            </Stack>
          </Stack>
        </Paper>

        {hasCompletionTabs && (
          <Tabs
            className="safe-completion-tabs"
            value={completionView}
            onChange={handleCompletionViewChange}
            aria-label="개선 이행 및 승인"
            variant="fullWidth"
            sx={{
              position: "sticky", top: 0, zIndex: 4, flexShrink: 0,
              bgcolor: "background.paper", border: "1px solid", borderColor: "divider",
              borderRadius: 1, minHeight: 56, boxShadow: "0 2px 6px rgba(30, 41, 59, 0.06)",
              "& .MuiTabs-indicator": { height: 3, bgcolor: "#6753c7" },
              "& .MuiTab-root": {
                minWidth: 0, maxWidth: "none", minHeight: 56, px: 1, py: 1.25,
                color: "#586170", transition: "background-color 120ms ease",
                "&:hover": { bgcolor: "#f5f6fa" },
                "&.Mui-selected": { color: "#4935a6", bgcolor: "#eeebff" },
                "&.Mui-focusVisible": { outline: "2px solid #6753c7", outlineOffset: -3 },
                "& .MuiSvgIcon-root": { display: { xs: "none", sm: "block" }, fontSize: 21, mr: 1 },
                "& .safe-completion-tab-label": { fontSize: "15px !important", fontWeight: "600 !important", lineHeight: 1.4 },
                "& .safe-completion-tab-count": {
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  minWidth: 26, height: 24, px: 0.6, borderRadius: 1, flexShrink: 0,
                  bgcolor: "#edf0f4", color: "#586170", fontSize: "13px !important", fontWeight: "600 !important",
                },
                "&.Mui-selected .safe-completion-tab-label": { fontWeight: "700 !important" },
                "&.Mui-selected .safe-completion-tab-count": { bgcolor: "#6753c7", color: "#fff" },
              },
            }}
          >
            {[['implementation', '개선대책 이행'], ['approval', '결과 확인·승인']].map(([value, label]) => (
              <Tab key={value} value={value} id={`completion-tab-${value}`}
                aria-controls={`completion-panel-${value}`}
                icon={value === 'implementation' ? <PlaylistAddCheckIcon /> : <FactCheckIcon />}
                iconPosition="start"
                label={<Stack direction="row" spacing={0.75} alignItems="center">
                  <span className="safe-completion-tab-label">{label}</span>
                  <Box component="span" className="safe-completion-tab-count">
                    {rows.filter((row) => matchesCompletionView(row, value)).length}
                  </Box>
                </Stack>} />
            ))}
          </Tabs>
        )}
        <Stack spacing={2} role={hasCompletionTabs ? "tabpanel" : undefined}
          id={hasCompletionTabs ? `completion-panel-${completionView}` : undefined}
          aria-labelledby={hasCompletionTabs ? `completion-tab-${completionView}` : undefined}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: step === 1 ? "repeat(3, minmax(0, 1fr))" : { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
            ...(step === 1 && { "& .safe-step-summary-hint": { display: { xs: "none", sm: "block" } } }),
            gap: 1.5,
          }}
        >
          {summary.map(({ key: summaryKey, ...item }) => (
            <StatCard
              key={summaryKey}
              {...item}
              active={activeSummaryFilter === summaryKey}
              onClick={() => handleSummaryClick({ key: summaryKey, ...item })}
            />
          ))}
        </Box>

        {feedback ? <Alert severity={feedback.severity}>{feedback.message}</Alert> : null}

        <Paper className="safe-step-work"
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            bgcolor: "#fff",
            overflow: "hidden",
          }}
        >
          <Stack className="safe-step-work-toolbar"
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
            spacing={1.25}
            sx={{ px: 2, py: 1.5 }}
          >
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
              {step === 1 ? <TextField select label="라인" size="small" value={selectedLines.length ? selectedLines : ["all"]}
                onChange={changeLines}
                SelectProps={{ multiple: true, renderValue: keys => {
                  const first = lineChoices.find(choice => choice.key === keys[0])?.label;
                  return keys.length > 1 ? `${first} 외 ${keys.length - 1}개` : first;
                } }}
                helperText={lineLoadFailed ? "라인 목록을 불러오지 못했습니다." : undefined}
                sx={{ width: { xs: "100%", sm: 208 }, flexShrink: 0 }}>
                {lineChoices.map(({ key, label }) => {
                  const checked = key === "all" ? !selectedLines.length : selectedLines.includes(key);
                  const Mark = checked ? CheckBoxIcon : CheckBoxOutlineBlankIcon;
                  return <MenuItem key={key} value={key}
                    sx={{ gap: 1, ...(key === "none" && { mt: 0.5, borderTop: 1, borderColor: "divider" }) }}>
                    <Mark aria-hidden sx={{ fontSize: 18, color: checked ? "primary.main" : "text.secondary" }} />
                    {label}
                  </MenuItem>;
                })}
              </TextField> : <Chip
                size="small"
                label={hasCompletionTabs ? completionView === "approval" ? "결과 확인·승인" : "개선대책 이행" : config.label}
                sx={{ bgcolor: `${config.tone}14`, color: config.tone, fontWeight: 900 }}
              />}
              {requestedCauseId && <Chip size="small" label="선택한 유해·위험요인" onDelete={() => {
                const next = new URLSearchParams(searchParams); next.delete('register_cause_id'); setSearchParams(next);
              }} />}
            </Stack>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="flex-end"
            >
              <TextField
                size="small"
                placeholder="설비·구역, 평가 대상, 위험요인 검색"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                sx={{
                  width: { xs: "100%", sm: 300 },
                  "& .MuiInputBase-root": { bgcolor: "#fff" },
                }}
              />
              {isLoading ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    불러오는 중
                  </Typography>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                  {displayedRows.length}/{viewRows.length}건
                </Typography>
              )}
            </Stack>
          </Stack>
          <Divider />

          {useCardLayout ? (
            <Box className="safe-step-work-list"
              sx={{
                p: { xs: 1.25, md: 1.5 },
                maxHeight: hasCompletionTabs ? "none" : { xs: "none", md: "calc(100vh - 360px)" },
                minHeight: hasCompletionTabs ? 0 : { xs: 280, md: 380 },
                overflowY: hasCompletionTabs ? "visible" : { xs: "visible", md: "auto" },
                bgcolor: "#f8fafc",
              }}
            >
              {displayedRows.length ? (
                <Stack spacing={1.25}>{displayedRows.map(renderRow)}</Stack>
              ) : (
                <Box
                  sx={{
                    minHeight: 320,
                    display: "grid",
                    placeItems: "center",
                    border: "1px dashed",
                    borderColor: "divider",
                    borderRadius: 2,
                    bgcolor: "#fff",
                  }}
                >
                  <Stack spacing={0.75} alignItems="center">
                    <Inventory2Icon sx={{ color: "text.disabled" }} />
                    <Typography sx={{ fontWeight: 900 }}>표시할 항목이 없습니다.</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {step === 1 ? "선택한 라인, 검색 조건과 기준 월을 확인하세요." : "이전 단계 진행 여부와 기준 월을 확인하세요."}
                    </Typography>
                  </Stack>
                </Box>
              )}
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: "calc(100vh - 360px)", minHeight: 380 }}>
              <Table stickyHeader size="small" sx={step === 1 ? {
                tableLayout: 'fixed', width: '100%', minWidth: 1180,
                '& th, & td': { px: '12px', minWidth: '0 !important', overflowWrap: 'anywhere', verticalAlign: 'middle' },
                '& tbody td': { py: '12px' },
                '& .MuiInputBase-root': { fontSize: 13 },
                '& .MuiChip-root': { maxWidth: '100%' },
                '& td .MuiChip-root': { height: 'auto', minHeight: 22 },
                '& td .MuiChip-label': { whiteSpace: 'normal', wordBreak: 'keep-all', lineHeight: 1.45, py: '3px' },
                '& th:last-child, & td:last-child': { position: 'sticky', right: 0, bgcolor: 'background.paper', zIndex: 1, borderLeft: '1px solid #e0e4e9' },
                '& th:last-child': { zIndex: 3 },
              } : undefined}>
                {step === 1 && <colgroup>
                  {(criteria.assessment_mode === 'grade' ? [180, undefined, 220, 120, 116] : [180, undefined, 156, 156, 184, 120, 116]).map((width, index) =>
                    <col key={index} style={width ? { width } : undefined} />)}
                </colgroup>}
                <TableHead>
                  <TableRow>
                    {config.columns.filter(column => !(step === 1 && criteria.assessment_mode === "grade" && ["중대성", "위험성"].includes(column))).map((column) => (
                      <TableCell
                        key={column}
                        align="center"
                        sx={{ bgcolor: "#f8fafc", fontWeight: 900, color: "text.secondary", whiteSpace: "nowrap" }}
                      >
                        {step === 1 && criteria.assessment_mode === "grade" && column === "가능성" ? "위험성 등급" : column}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedRows.length ? (
                    displayedRows.map(renderRow)
                  ) : (
                    <EmptyRows
                      colSpan={config.columns.length}
                      title="표시할 항목이 없습니다."
                      body={step === 1 ? "선택한 라인, 검색 조건과 기준 월을 확인하세요." : "이전 단계 진행 여부와 기준 월을 확인하세요."}
                    />
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
        </Stack>
        {step === 1 ? (
          <RiskHistoryDialog
            open={Boolean(step1DetailRow)}
            row={step1DetailRow}
            criteria={criteria}
            onClose={() => setStep1DetailRow(null)}
          />
        ) : null}
      </Stack>
    </Box>
    </AssessmentModeContext.Provider>
  );
}

export function AssessmentStep1Page() {
  return <RiskAssessmentStepPage step={1} />;
}

export function AssessmentStep2Page() {
  return <RiskAssessmentStepPage step={2} />;
}

export function AssessmentStep3Page() {
  return <RiskAssessmentStepPage step={3} />;
}
